import { useState } from "react";
import { Check, Sparkles, Building2, Boxes, Landmark, Mail, Minus, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { PoweredBy, LegalLinks } from "@/components/Branding";
import { startCheckout } from "@/lib/api";
import { toast } from "sonner";

const SALES_EMAIL = "hello@cbkm.au";

const TIERS = [
  {
    id: "starter", name: "Starter", icon: Boxes, monthly: 0, annual: 0,
    tagline: "Read & validate, free forever",
    features: ["Upload & parse ADAC XML", "Reader & asset inspection", "Schema validation + validation PDF", "1 local LGA schema", "Single user"],
    cta: "current", priced: true,
  },
  {
    id: "single", name: "Single Authority", icon: Sparkles, monthly: 29, annual: 290, highlight: true,
    tagline: "One authority, per seat",
    monthlyKey: "single_authority_monthly", yearlyKey: "single_authority_yearly",
    features: ["Everything in Starter", "1 chosen authority QA schema", "QA tolerance + per-type profiles", "ADAC Builder, Compare, Map", "Long & cross-section profiles", "TIN surfaces + cut/fill volumes", "Council Submission Pack PDF"],
    cta: "buy", priced: true,
  },
  {
    id: "all", name: "All Authorities", icon: Building2, monthly: 49, annual: 490,
    tagline: "Every authority, per seat",
    monthlyKey: "all_authorities_monthly", yearlyKey: "all_authorities_yearly",
    features: ["Everything in Single Authority", "All authority QA schemas", "Switch authorities anytime", "Priority email support"],
    cta: "buy", priced: true,
  },
  {
    id: "enterprise", name: "Enterprise", icon: Landmark,
    tagline: "Volume seats + SSO",
    features: ["All schemas", "Volume seat licensing", "SSO & central seat management", "Onboarding & priority support"],
    cta: "sales", priced: false,
  },
  {
    id: "council", name: "Council", icon: Landmark,
    tagline: "Unlimited, annual",
    features: ["Unlimited schemas", "Unlimited seats", "Annual agreement", "Dedicated support"],
    cta: "sales", priced: false,
  },
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8">
        <a href="/" className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-sm bg-blue-600 flex items-center justify-center"><Boxes className="w-4 h-4 text-white" /></div>
          <span className="font-display text-base font-bold tracking-tight">ADAC Suite</span>
        </a>
        <a href="/" className="font-mono text-[11px] text-zinc-400 hover:text-zinc-200">← Back to app</a>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">Plans &amp; pricing</h1>
          <p className="font-mono text-[12px] text-zinc-500">Per-seat, month-to-month. Built for surveyors, civil consultants &amp; council QA.</p>
        </div>

        {/* Monthly / annual toggle */}
        <div className="flex items-center justify-center gap-3 mb-10" data-testid="billing-toggle">
          <span className={`font-mono text-[12px] ${!annual ? "text-zinc-100" : "text-zinc-500"}`}>Monthly</span>
          <button
            data-testid="toggle-annual"
            onClick={() => setAnnual((a) => !a)}
            className={`relative w-12 h-6 rounded-full transition-colors duration-150 ${annual ? "bg-blue-600" : "bg-zinc-700"}`}
            aria-label="Toggle annual billing"
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-150 ${annual ? "translate-x-6" : ""}`} />
          </button>
          <span className={`font-mono text-[12px] ${annual ? "text-zinc-100" : "text-zinc-500"}`}>Annual</span>
          <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-emerald-600/15 text-emerald-400 border border-emerald-600/30">~2 months free</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {TIERS.map((t) => (
            <PlanCard key={t.id} tier={t} annual={annual} user={user} />
          ))}
        </div>

        <p className="text-center font-mono text-[10px] text-zinc-600 mt-8">
          All prices in <span className="text-zinc-400">AUD, ex-GST</span> (10% GST added at checkout for AU customers). Cancel anytime — month-to-month, no lock-in.
        </p>
        <div className="flex flex-col items-center gap-2 mt-8 pt-6 border-t border-zinc-800">
          <PoweredBy />
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}

function PlanCard({ tier, annual, user }) {
  const { id, name, icon: Icon, tagline, features, cta, highlight } = tier;
  const amount = tier.priced ? (annual ? tier.annual : tier.monthly) : null;
  const period = !tier.priced ? "" : amount === 0 ? "free" : annual ? "/ user / yr" : "/ user / mo";
  const isCurrent = id === "starter" && (!user || user.plan === "free");
  const [seats, setSeats] = useState(1);
  const [busy, setBusy] = useState(false);
  const activeHere = user && user.plan === id;

  const subscribe = async () => {
    if (!user) { window.location.href = "/login?next=/pricing"; return; }
    setBusy(true);
    try {
      const lookup_key = annual ? tier.yearlyKey : tier.monthlyKey;
      const { url } = await startCheckout(lookup_key, seats, window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  const button = () => {
    if (cta === "current") {
      return <Button disabled variant="outline" className="w-full rounded-sm border-zinc-700 bg-transparent text-zinc-500" data-testid={`plan-cta-${id}`}>{isCurrent ? "Current plan" : "Included"}</Button>;
    }
    if (cta === "sales") {
      return (
        <a href={`mailto:${SALES_EMAIL}?subject=ADAC Suite — ${name} enquiry`} data-testid={`contact-${id}`} className="block">
          <Button variant="outline" className="w-full rounded-sm border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900"><Mail className="w-4 h-4 mr-2" /> Contact sales</Button>
        </a>
      );
    }
    if (activeHere) {
      return <Button disabled variant="outline" className="w-full rounded-sm border-emerald-600/40 bg-emerald-600/10 text-emerald-400" data-testid={`plan-cta-${id}`}>Your current plan</Button>;
    }
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2" data-testid={`seats-${id}`}>
          <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Seats</span>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setSeats((s) => Math.max(1, s - 1))} data-testid={`seats-dec-${id}`} className="w-6 h-6 rounded-sm border border-zinc-700 flex items-center justify-center text-zinc-300 hover:bg-zinc-800"><Minus className="w-3 h-3" /></button>
            <span className="w-8 text-center font-mono text-sm" data-testid={`seats-count-${id}`}>{seats}</span>
            <button onClick={() => setSeats((s) => Math.min(500, s + 1))} data-testid={`seats-inc-${id}`} className="w-6 h-6 rounded-sm border border-zinc-700 flex items-center justify-center text-zinc-300 hover:bg-zinc-800"><Plus className="w-3 h-3" /></button>
          </div>
        </div>
        <Button onClick={subscribe} disabled={busy} data-testid={`subscribe-${id}`} className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Subscribe — A$${amount * seats}${annual ? "/yr" : "/mo"}`}
        </Button>
      </div>
    );
  };

  return (
    <div data-testid={`plan-${id}`} className={`relative rounded-sm p-5 flex flex-col ${highlight ? "border-2 border-blue-600/50 bg-blue-600/[0.04]" : "border border-zinc-800 bg-[#101010]"}`}>
      {highlight && <span className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-blue-600 text-white">Popular</span>}
      <div className="flex items-center gap-2 mb-1 pr-16">
        {Icon && <Icon className={`w-4 h-4 shrink-0 ${highlight ? "text-blue-400" : "text-zinc-400"}`} />}
        <h3 className="font-display text-base font-bold tracking-tight">{name}</h3>
      </div>
      <p className="font-mono text-[10px] text-zinc-500 mb-3 min-h-[28px]">{tagline}</p>
      <div className="flex items-baseline gap-1 mb-5 min-h-[40px]">
        {tier.priced ? (
          <>
            <span className="font-display text-2xl font-bold">A${amount}</span>
            <span className="font-mono text-[10px] text-zinc-500">{period}</span>
          </>
        ) : (
          <span className="font-display text-xl font-bold text-zinc-300">Custom</span>
        )}
      </div>
      <ul className="space-y-2 flex-1 mb-5">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 font-mono text-[11px] text-zinc-300">
            <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${highlight ? "text-blue-400" : "text-emerald-500"}`} /> {f}
          </li>
        ))}
      </ul>
      {button()}
    </div>
  );
}
